// Shared "request work from an agent" dialog — the RequestDialog machinery
// extracted out of Apps.jsx so BOTH the Apps page and the Agents page use the
// same modal, POST /api/app_requests plumbing, and kind-tab UX. It is fully
// driven by the `kinds` metadata passed in, so each page supplies its own set
// (apps: new_app / change / bug_fix; agents: new_agent / change_agent) without
// the component knowing anything app- or agent-specific.
//
// POST body contract (backend /api/app_requests): { kind, title, details?,
// app_id? (change/bug_fix), agent_id? (change_agent) }. The X-Cortex-Project
// header is added automatically by fetchapi from the URL — no caller wiring.
import React, { useEffect, useState } from "react";
import { Loader2, Check, X, ChevronLeft, Sparkles, Plus, Users, User } from "lucide-react";
import { createAppRequest, proposeRequestFeatures } from "../services/apps";
import { btnPrimary, btnGhost, fieldCls, sectionLabel } from "./board/requestMeta";
import AttachmentPicker, { prepareAttachments } from "./board/AttachmentPicker";

// EVERY request kind accepts an optional file (set `allowFile: false` on a
// kind to opt out). The pick/upload contract lives in the shared
// AttachmentPicker (board/AttachmentPicker.jsx) — every file uploads to S3
// and rides as an `attachments` reference; this dialog only threads it
// through.

// Field/button styling comes from the shared form tokens (requestMeta) —
// the same language as the task modal and Feed's Add to Board.
const inputCls = fieldCls + " w-full";

// `kinds`: ordered array of { kind, label, icon, hint, description?,
//   needsTarget?, targetLabel?, titlePlaceholder?, detailsPlaceholder?,
//   allowFile?, fileHint?, noProject? }. allowFile → an optional document
//   (.docx/.txt/.md/.csv) whose extracted text is appended to the details
//   text on submit. needsTarget → renders a <select> over `targets` and
//   requires a selection; the selected id is sent as `targetField` (default
//   'app_id').
// `targets`: [{ id, label }] choices for needsTarget kinds.
// `preset`: { kind?, title?, details?, targetId? } to prefill / lock a kind.
// `kindLayout`: "tabs" (default) or "select" — a compact <select> of kinds
//   with the chosen kind's `description` shown under it (the Board's New task
//   dialog).
// `projects` + `defaultProject`: when given, renders a project <select> and
//   files the request into the chosen project (cross-project surfaces only;
//   kinds with `noProject` — e.g. new_project — hide it).
// `presetParent`: programmatic-only parent link (the UI picker is retired —
// linked-tasks authoring left the product; agent-filed follow-ups still
// arrive parent-linked via the API). `parentCandidates`: legacy, renders an optional
//   "Attach to task" <select> over the caller's own tasks; the choice rides
//   as parent_id (Board task links).
// `loadTargets(kind, projectSlug) => Promise<[{id,label}]>`: dynamic target
//   loading for needsTarget kinds on cross-project surfaces (the Board) —
//   refetched when the kind or the selected project changes; static `targets`
//   keep working for in-project pages. Kinds may carry `group` (renders
//   <optgroup>s in the select layout), `targetField` (overrides the dialog-
//   level one — the Board mixes app_id and agent_id kinds in one dialog) and
//   `projectHint` (subtitle under the project select).
// The kind is ALWAYS the user's explicit choice. An LLM classifier ("Let the
// agent decide") shipped twice and was removed twice — the second time after
// prod request #95, where a multi-deliverable brief filed from a project was
// classified new_project and built into a SEPARATE project. The "select"
// layout therefore opens on a disabled "Select a type…" placeholder and
// refuses to submit until a concrete kind is picked.
export default function AppRequestDialog({
  kinds,
  targets = [],
  targetField = "app_id",
  preset = {},
  title: modalTitle = "Build with an agent",
  // 'todo' files the request PARKED on the Board's To Do column instead of
  // straight into the worker queue (the Board's "New task" button).
  initialStatus = null,
  kindLayout = "tabs",
  projects = null,
  defaultProject = null,
  parentCandidates = null,
  presetParent = null,
  loadTargets = null,
  submitLabel = "Submit build",
  // "large" widens the modal and gives the details textarea real room —
  // the Board's New task dialog, where the brief IS the work order.
  size = "default",
  // When this dialog was reached FROM another modal, `onBack` returns there
  // instead of dumping the user back to the page. Without it a nested route is
  // a dead end: Cancel closes everything and the step before is unreachable.
  onBack = null,
  onClose,
  onCreated,
}) {
  // The select layout starts UNSELECTED — the type is an explicit decision,
  // not a default the user scrolls past. Tabs keep their first-tab default
  // (the opening button already names the kind, e.g. Apps' "Build an app").
  const [kind, setKind] = useState(preset.kind || (kindLayout === "select" ? "" : kinds[0]?.kind));
  const [targetId, setTargetId] = useState(preset.targetId ?? null);
  const [title, setTitle] = useState(preset.title || "");
  const [details, setDetails] = useState(preset.details || "");
  const [projectSlug, setProjectSlug] = useState(defaultProject || null);
  const [parentId, setParentId] = useState(presetParent ?? null);
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState(null);
  const [files, setFiles] = useState([]); // [{ name, size, blob }] — uploads on submit
  const [textTarget, setTextTarget] = useState(""); // string-keyed target (see textTargets)
  // Multi-tenancy (kinds flagged `tenancyChoice` — the ones that deliver a
  // user-facing app): 'shared' (default, everyone works with the same data)
  // or 'multi' (each signed-in user sees only their own rows). Rides in the
  // details text as a TENANCY line the builder skills act on.
  const [tenancy, setTenancy] = useState("shared");
  // Generated feature checklist (kinds flagged `suggestFeatures`): null until
  // generated, then { groups: [{ label, options: [{ id, label, description,
  // checked }] }] }. `customItems` holds user-typed extras (always built).
  const [feat, setFeat] = useState(null);
  const [featBusy, setFeatBusy] = useState(false);
  const [customItems, setCustomItems] = useState([]);
  const [customDraft, setCustomDraft] = useState("");

  const meta = kinds.find((k) => k.kind === kind) || null;
  const needsTarget = !!meta?.needsTarget;
  const allowFile = meta?.allowFile !== false; // attachments are universal; opt out per kind
  const askTenancy = !!meta?.tenancyChoice;
  const canSuggest = !!meta?.suggestFeatures;
  // A kind with textTargets requires a pick, exactly as needsTarget does — and
  // when the list is EMPTY there is nothing valid to submit at all.
  const textTargetOk = !meta?.textTargets || (meta.textTargets.length > 0 && !!textTarget);
  const canSubmit = !!meta && title.trim() && (!needsTarget || targetId != null) && textTargetOk;
  // Switching kind must clear a pick that belongs to the previous kind — and
  // the generated checklist/tenancy answer, which described the previous kind.
  useEffect(() => { setTextTarget(""); setFeat(null); setCustomItems([]); setTenancy("shared"); }, [kind]);

  const toggleOption = (gi, oi) => {
    setFeat((prev) => {
      if (!prev) return prev;
      const groups = prev.groups.map((g, i) => i !== gi ? g : {
        ...g,
        options: g.options.map((o, j) => j !== oi ? o : { ...o, checked: !o.checked }),
      });
      return { ...prev, groups };
    });
  };
  const generateFeatures = async () => {
    setFeatBusy(true); setErr(null);
    try {
      const res = await proposeRequestFeatures({
        kind,
        title: title.trim(),
        details: details.trim(),
        attachment_names: files.map((f) => f.name),
        ...(askTenancy ? { exclude_tenancy: true } : {}),
      }, { projectSlug: projects && !meta?.noProject ? projectSlug : null });
      setFeat({ groups: res?.groups || [] });
    } catch (e) {
      setErr(e?.message || String(e));
    } finally { setFeatBusy(false); }
  };
  // The checklist answer travels IN the details text — the same shape the
  // chat widget produces ("Selected features: …"), so every builder skill
  // reads it without new plumbing.
  const featureLines = () => {
    if (!feat && !customItems.length) return "";
    const on = [], off = [];
    for (const g of feat?.groups || []) for (const o of g.options) (o.checked ? on : off).push(o.label);
    const parts = [];
    if (on.length) parts.push("Selected features:\n" + on.map((l) => `- ${l}`).join("\n"));
    if (off.length) parts.push("Explicitly deselected (do NOT build):\n" + off.map((l) => `- ${l}`).join("\n"));
    if (customItems.length) parts.push("My own additional items (first-class requirements, verbatim):\n" + customItems.map((l) => `- ${l}`).join("\n"));
    return parts.join("\n\n");
  };

  // Dynamic targets (Board): refetch when the kind's target type or the
  // selected project changes; a stale selection not present in the fresh
  // list is dropped so a project switch can't smuggle the wrong id through.
  const [dynTargets, setDynTargets] = useState(null); // null = loading
  useEffect(() => {
    if (!loadTargets || !needsTarget) return;
    let alive = true;
    setDynTargets(null);
    loadTargets(kind, projectSlug)
      .then((list) => {
        if (!alive) return;
        const opts = Array.isArray(list) ? list : [];
        setDynTargets(opts);
        if (opts.length === 1) {
          // A single candidate needs no picking — land preselected.
          setTargetId(opts[0].id);
        } else {
          setTargetId((prev) => (opts.some((t) => t.id === prev) ? prev : null));
        }
      })
      .catch(() => { if (alive) setDynTargets([]); });
    return () => { alive = false; };
  }, [loadTargets, needsTarget, kind, projectSlug]);
  const targetOptions = loadTargets ? (dynTargets || []) : targets;
  const targetsLoading = !!loadTargets && needsTarget && dynTargets === null;

  const submit = async () => {
    setPending(true); setErr(null);
    try {
      const prep = allowFile ? await prepareAttachments(files) : {};
      // Prefix the chosen string-keyed target onto details so the executor can
      // resolve it without a dedicated column (the change_workflow precedent).
      let body = textTarget
        ? `${meta.textTargetPrefix || "TARGET"}: ${textTarget}\n\n${details.trim()}`.trim()
        : details.trim();
      // The tenancy answer and the ticked checklist ride in the details text —
      // plain language the builder skills already read; no new columns.
      if (askTenancy && tenancy === "multi") {
        body = `TENANCY: multi-tenant — every signed-in user must see ONLY their own data. Enforce per-user isolation SERVER-SIDE in the app's data endpoints (verify the caller's session token, filter every read and write by that user), never in the browser.\n\n${body}`.trim();
      }
      const featBlock = featureLines();
      if (featBlock) body = `${body}\n\n${featBlock}`.trim();
      await createAppRequest({
        kind,
        title: title.trim(),
        details: body || undefined,
        ...(prep.attachments ? { attachments: prep.attachments } : {}),
        ...(needsTarget ? { [meta?.targetField || targetField]: targetId } : {}),
        ...(initialStatus ? { initial_status: initialStatus } : {}),
        ...(parentId != null ? { parent_id: parentId } : {}),
      }, { projectSlug: projects && !meta?.noProject ? projectSlug : null });
      onCreated();
    } catch (e) { setErr(e?.message || String(e)); setPending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        className="relative bg-card-bg border border-card-border rounded-xl shadow-xl p-5"
        style={{ width: size === "large" ? 680 : 500, maxWidth: "calc(100vw - 32px)", maxHeight: "85vh", overflowY: "auto" }}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-2 min-w-0">
            {onBack && (
              <button type="button" onClick={onBack} data-testid="request-back" title="Back"
                className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-0.5 -ml-1 mt-0.5">
                <ChevronLeft size={16} />
              </button>
            )}
            <div className="min-w-0">
            <div className="font-semibold text-page-text">{modalTitle}</div>
            {/* Same header pattern as Feed's Add to Board: title + one muted
                line saying where the request lands. */}
            {initialStatus === "todo" && (
              <div className="text-sm text-page-muted mt-0.5">Lands in the Board's To Do column.</div>
            )}
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1">
            <X size={16} />
          </button>
        </div>

        {/* Cross-project surfaces (the Board): pick which project the request
            is filed into, FIRST — it frames everything below. Hidden for
            kinds that create their own project. */}
        {projects && !meta?.noProject && (
          <div className="mb-3">
            <div className={sectionLabel + " mb-1"}>Project</div>
            <select
              data-testid="app-request-project"
              className={inputCls}
              value={projectSlug ?? ""}
              onChange={(e) => setProjectSlug(e.target.value || null)}
            >
              {projects.map((p) => (
                <option key={p.slug} value={p.slug}>{p.name || p.slug}</option>
              ))}
            </select>
            <div className="text-xs text-page-muted mt-1">{meta?.projectHint || "The build will happen in this project."}</div>
          </div>
        )}
        {kinds.length > 1 && kindLayout === "select" && (
          <div className="mb-3">
            <div className={sectionLabel + " mb-1"}>Type</div>
            <select
              data-testid="app-request-kind"
              className={inputCls}
              value={kind}
              onChange={(e) => setKind(e.target.value)}
            >
              <option value="" disabled>Select a type…</option>
              {(() => {
                // Preserve declaration order; kinds without a group render flat.
                const groups = [];
                for (const k of kinds) {
                  const g = k.group || "";
                  const last = groups[groups.length - 1];
                  if (last && last.name === g) last.items.push(k);
                  else groups.push({ name: g, items: [k] });
                }
                return groups.map((g, i) => g.name
                  ? (
                    <optgroup key={i} label={g.name}>
                      {g.items.map((k) => <option key={k.kind} value={k.kind}>{k.label}</option>)}
                    </optgroup>
                  )
                  : g.items.map((k) => <option key={k.kind} value={k.kind}>{k.label}</option>));
              })()}
            </select>
            {(meta?.description || meta?.hint) && (
              <div className="text-xs text-page-muted mt-1">{meta.description || meta.hint}</div>
            )}
          </div>
        )}
        {kinds.length > 1 && kindLayout !== "select" && (
          <div className="flex gap-1.5 mb-4">
            {kinds.map((k) => {
              const Icon = k.icon;
              const active = kind === k.kind;
              return (
                <button
                  key={k.kind}
                  type="button"
                  data-testid={`app-request-kind-${k.kind}`}
                  onClick={() => setKind(k.kind)}
                  className={
                    "flex-1 rounded-md px-2 py-2 text-sm inline-flex items-center justify-center gap-1.5 cursor-pointer border " +
                    (active ? "border-nav-active bg-nav-active/10 text-nav-active font-medium" : "border-card-border bg-transparent text-page-text hover:bg-card-border/30")
                  }
                >
                  {Icon && <Icon size={14} />} {k.label}
                </button>
              );
            })}
          </div>
        )}
        {meta?.hint && kindLayout !== "select" && <div className="text-xs text-page-muted mb-3">{meta.hint}</div>}

        {/* DATA VISIBILITY — only for kinds that deliver a user-facing app
            (`tenancyChoice`). The FIRST decision of the form: is this a shared
            workspace, or does every signed-in user get their own data? The
            answer rides as a TENANCY line in the brief. */}
        {askTenancy && (
          <div className="mb-3">
            <div className={sectionLabel + " mb-1"}>Data visibility</div>
            <div className="grid grid-cols-2 gap-1.5">
              {[
                { v: "shared", icon: Users, label: "Shared", note: "Every user works with the same data" },
                { v: "multi", icon: User, label: "Multi-tenant", note: "Each signed-in user sees only their own data" },
              ].map(({ v, icon: Icon, label, note }) => (
                <button
                  key={v}
                  type="button"
                  data-testid={`app-request-tenancy-${v}`}
                  onClick={() => setTenancy(v)}
                  className={
                    "rounded-md px-2.5 py-2 text-left cursor-pointer border " +
                    (tenancy === v ? "border-nav-active bg-nav-active/10" : "border-card-border bg-transparent hover:bg-card-border/30")
                  }
                >
                  <div className={"text-sm inline-flex items-center gap-1.5 " + (tenancy === v ? "text-nav-active font-medium" : "text-page-text")}>
                    <Icon size={14} /> {label}
                  </div>
                  <div className="text-[11px] text-page-muted mt-0.5">{note}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STRING-KEYED targets (a connector is keyed by node_type, not an id
            column) ride in the details text — the same way change_workflow
            embeds its target definition. When there are none, say WHY and offer
            the way forward instead of showing a form that cannot be satisfied. */}
        {meta?.textTargets && meta.textTargets.length === 0 && (
          <div data-testid="no-targets-notice"
            className="rounded-md border border-card-border bg-page-bg/60 p-3 mb-3 text-sm text-page-text">
            <div className="font-medium mb-1">{meta.emptyTitle || "Nothing to change yet"}</div>
            <div className="text-xs text-page-muted">{meta.emptyHint}</div>
            {meta.emptyActionKind && (
              <button type="button" onClick={() => setKind(meta.emptyActionKind)}
                className={btnPrimary + " mt-3"}>{meta.emptyActionLabel || "Build a new one"}</button>
            )}
          </div>
        )}
        {meta?.textTargets && meta.textTargets.length > 0 && (
          <select
            data-testid="app-request-text-target"
            className={inputCls + " mb-3"}
            value={textTarget}
            onChange={(e) => setTextTarget(e.target.value)}
          >
            <option value="">{meta.targetLabel || "Select…"}</option>
            {meta.textTargets.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        )}

        {needsTarget && (
          <>
          {kindLayout === "select" && <div className={sectionLabel + " mb-1"}>Target</div>}
          <select
            data-testid="app-request-target"
            className={inputCls + " mb-3"}
            value={targetId ?? ""}
            disabled={targetsLoading}
            onChange={(e) => setTargetId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">
              {targetsLoading
                ? "Loading…"
                : (targetOptions.length === 0 && loadTargets
                  ? "Nothing to change in this project"
                  : (meta.targetLabel || "Select…"))}
            </option>
            {targetOptions.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          </>
        )}
        {kindLayout === "select" && <div className={sectionLabel + " mb-1"}>Title</div>}
        <input
          autoFocus
          data-testid="app-request-title"
          className={inputCls + " mb-3"}
          placeholder={meta?.titlePlaceholder || "Short summary"}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        {kindLayout === "select" && <div className={sectionLabel + " mb-1"}>Brief</div>}
        <textarea
          data-testid="app-request-details"
          className={inputCls + " mb-1 resize-y " + (size === "large" ? "min-h-[240px]" : "min-h-[110px]")}
          placeholder={meta?.detailsPlaceholder || "Describe what you need in plain language…"}
          value={details}
          onChange={(e) => setDetails(e.target.value)}
        />
        {allowFile && (
          <div className="mt-2">
            <AttachmentPicker files={files} onChange={setFiles} onError={setErr} hint={meta?.fileHint} testId="app-request-file" />
          </div>
        )}

        {/* GENERATED FEATURE CHECKLIST — the form twin of the chat's
            propose_features widget (kinds flagged `suggestFeatures`). Once the
            user has described the ask (or attached files), one click invents
            the option list; the ticked/unticked state and any user-typed extras
            ride in the brief exactly like the chat card's answer. */}
        {canSuggest && (
          <div className="mt-3">
            {!feat && (
              <button
                type="button"
                data-testid="app-request-suggest"
                className={btnGhost + " text-xs"}
                disabled={featBusy || (!details.trim() && !title.trim() && files.length === 0)}
                onClick={generateFeatures}
                title="An agent reads the brief and proposes the feature options to tick before filing"
              >
                {featBusy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                {featBusy ? "Reading the brief…" : "Suggest options to pick"}
              </button>
            )}
            {feat && (
              <div className="rounded-md border border-card-border p-3 space-y-3" data-testid="app-request-features">
                {feat.groups.map((g, gi) => (
                  <div key={gi}>
                    <div className={sectionLabel + " mb-1"}>{g.label}</div>
                    <div className="space-y-1">
                      {g.options.map((o, oi) => (
                        <label key={o.id} className="flex items-start gap-2 text-sm cursor-pointer rounded px-1 py-0.5 hover:bg-card-border/20">
                          <input type="checkbox" className="mt-0.5 cursor-pointer" checked={!!o.checked} onChange={() => toggleOption(gi, oi)} />
                          <span>
                            <span className="text-page-text">{o.label}</span>
                            {o.description && <span className="text-xs text-page-muted"> — {o.description}</span>}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                {customItems.length > 0 && (
                  <div>
                    <div className={sectionLabel + " mb-1"}>Your own items</div>
                    {customItems.map((c, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-page-text px-1 py-0.5">
                        <Check size={13} className="text-nav-active shrink-0" />
                        <span className="flex-1">{c}</span>
                        <button type="button" className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-0.5"
                          onClick={() => setCustomItems(customItems.filter((_, j) => j !== i))}>
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <input
                    className={fieldCls + " flex-1 text-sm"}
                    placeholder="Add your own item…"
                    value={customDraft}
                    data-testid="app-request-custom-item"
                    onChange={(e) => setCustomDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && customDraft.trim()) {
                        e.preventDefault();
                        setCustomItems([...customItems, customDraft.trim()]);
                        setCustomDraft("");
                      }
                    }}
                  />
                  <button type="button" className={btnGhost + " text-xs"} disabled={!customDraft.trim()}
                    onClick={() => { setCustomItems([...customItems, customDraft.trim()]); setCustomDraft(""); }}>
                    <Plus size={13} /> Add
                  </button>
                </div>
                <button type="button" className={btnGhost + " text-xs"} disabled={featBusy} onClick={generateFeatures}>
                  {featBusy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Regenerate
                </button>
              </div>
            )}
          </div>
        )}
        {err && <div className="text-sm text-red-500 mt-2">{err}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" className={btnGhost} onClick={onClose}>Cancel</button>
          <button type="button" data-testid="app-request-submit" className={btnPrimary} disabled={pending || !canSubmit} onClick={submit}>
            {pending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
